import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Sidebar from '../components/layout/Sidebar';
import RightBar from '../components/layout/RightBar';
import PostForm from '../components/posts/PostForm';
import PostCard from '../components/posts/PostCard';
import Avatar from '../components/common/Avatar';
import Button from '../components/common/Button';
import Loader from '../components/common/Loader';
import useAuth from '../hooks/useAuth';
import { getCommunityByIdOrSlug, joinCommunity, leaveCommunity } from '../api/communities';
import { getCommunityPosts } from '../api/posts';

/**
 * CommunityDetail page for viewing a single community
 * @returns {JSX.Element} CommunityDetail page component
 */
const CommunityDetail = () => {
  const { slug } = useParams();
  const { user, isAuth } = useAuth();
  
  const [community, setCommunity] = useState(null);
  const [posts, setPosts] = useState([]);
  const [isMember, setIsMember] = useState(false);
  const [activeTab, setActiveTab] = useState('posts');
  const [loading, setLoading] = useState({
    community: true,
    posts: false,
  });
  const [error, setError] = useState({
    community: '',
    posts: '',
  });
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  
  // Fetch community data
  useEffect(() => {
    const fetchCommunityData = async () => {
      try {
        setLoading((prev) => ({ ...prev, community: true }));
        setError((prev) => ({ ...prev, community: '' }));
        
        const response = await getCommunityByIdOrSlug(slug);
        console.log("Rxyz : ", response.data.data.community)
        setCommunity(response.data.data.community);
        
        // Check if current user is a member
        // if (isAuth && user && response.data.community.members) {
        //   setIsMember(response.data.community.members.includes(user._id));
        // }
        if(response.data) {
          setIsMember(true);
        }
        
      } catch (error) {
        console.error('Error fetching community:', error);
        setError((prev) => ({
          ...prev,
          community: 'Failed to load community. Please try again.',
        }));
      } finally {
        setLoading((prev) => ({ ...prev, community: false }));
      }
    };
    
    if (slug) {
      fetchCommunityData();
    }
  }, [slug, isAuth, user]);
  
  // Fetch community posts when community loads or tab changes
  useEffect(() => {
    if (community && activeTab === 'posts') {
      fetchCommunityPosts(1);
    }
  }, [community, activeTab]);
  
  // Fetch community posts
  const fetchCommunityPosts = async (pageNum) => {
    if (!community) return;
    
    try {
      setLoading((prev) => ({ ...prev, posts: true }));
      setError((prev) => ({ ...prev, posts: '' }));
      
      const response = await getCommunityPosts(community._id, {
        page: pageNum,
        limit: 10,
      });
      
      if (pageNum === 1) {
        setPosts(response.data.data.posts);
      } else {
        setPosts((prev) => [...prev, ...response.data.data.posts]);
      }
      
      // Check if there are more posts to load
      setHasMore(
        response.data.pagination.page < response.data.pagination.pages
      );
      setPage(response.data.pagination.page);
    } catch (error) {
      console.error('Error fetching community posts:', error);
      setError((prev) => ({
        ...prev,
        posts: 'Failed to load posts. Please try again.',
      }));
    } finally {
      setLoading((prev) => ({ ...prev, posts: false }));
    }
  };
  
  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };
  
  // Handle join/leave community
  const handleMembershipToggle = async () => {
    if (!isAuth || !community) return;
    
    try {
      if (isMember) {
        await leaveCommunity(community._id);
      } else {
        await joinCommunity(community._id);
      }
      
      setIsMember(!isMember);
      
      // Update member count
      setCommunity((prev) => ({
        ...prev,
        memberCount: isMember
          ? (prev.memberCount || 1) - 1
          : (prev.memberCount || 0) + 1,
      }));
    } catch (error) {
      console.error('Error toggling membership:', error);
    }
  };
  
  // Handle new post creation
  const handlePostCreated = () => {
    // Refresh posts
    fetchCommunityPosts(1);
  };
  
  // Load more posts
  const handleLoadMore = () => {
    if (hasMore && !loading.posts) {
      fetchCommunityPosts(page + 1);
    }
  };
  
  // Render loading state
  if (loading.community && !community) {
    return (
      <div className="bg-gray-100 min-h-screen">
        {/* <Header /> */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex justify-center py-12">
            <Loader size="lg" />
          </div>
        </div>
      </div>
    );
  }
  
  // Render error state
  if (error.community && !community) {
    return (
      <div className="bg-gray-100 min-h-screen">
        {/* <Header /> */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error.community}
          </div>
        </div>
      </div>
    );
  }
  
  // Render community not found
  if (!community) {
    return (
      <div className="bg-gray-100 min-h-screen">
        {/* <Header /> */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white border border-gray-200 rounded-lg p-6 text-center">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
              />
            </svg>
            <h3 className="mt-2 text-lg font-medium text-gray-900">Community not found</h3>
            <p className="mt-1 text-sm text-gray-500">
              The community you're looking for doesn't exist or has been removed.
            </p>
            <div className="mt-6">
              <Link to="/communities">
                <Button variant="primary">Browse Communities</Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // Check if community is private and user is not a member
  const isPrivateAndNotMember = community.isPrivate && !isMember;
  
  return (
    <div className="bg-gray-100 min-h-screen">
      {/* <Header /> */}
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col md:flex-row gap-6">
          {/* Left sidebar */}
          <div className="hidden md:block md:w-64 lg:w-72">
            {/* <Sidebar /> */}
          </div>
          
          {/* Main content */}
          <div className="flex-1 space-y-6">
            {/* Community header */}
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              {/* Cover image */}
              <div className="h-48 bg-gray-200 relative">
                {community.coverPicture ? (
                  <img
                    src={community.coverPicture}
                    alt={`${community.name} cover`}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-r from-blue-400 to-indigo-500"></div>
                )}
                
                {/* Community profile image */}
                <div className="absolute bottom-0 left-8 transform translate-y-1/2">
                  <div className="w-20 h-20 bg-white rounded-full p-1">
                    <div className="w-full h-full bg-gray-200 rounded-full overflow-hidden">
                      {community.profilePicture ? (
                        <img
                          src={community.profilePicture}
                          alt={community.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-blue-500 flex items-center justify-center text-white text-xl font-medium">
                          {community.name.charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Private badge */}
                {community.isPrivate && (
                  <div className="absolute top-2 right-2 bg-gray-900 bg-opacity-50 text-white px-2 py-1 rounded-full text-xs font-medium">
                    Private
                  </div>
                )}
                
                {/* Join/Leave button */}
                {isAuth && (
                  <div className="absolute bottom-4 right-4">
                    <Button
                      variant={isMember ? 'outline' : 'primary'}
                      onClick={handleMembershipToggle}
                    >
                      {isMember ? 'Leave' : 'Join'}
                    </Button>
                  </div>
                )}
              </div>
              
              {/* Community info */}
              <div className="pt-12 px-8 pb-6">
                <h1 className="text-2xl font-bold text-gray-900">{community.name}</h1>
                <p className="text-gray-500 mt-1">
                  {community.memberCount || 0} members
                </p>
                
                <p className="mt-4 text-gray-700">{community.description}</p>
                
                {/* Creator info */}
                {community.creator && (
                  <div className="mt-4 flex items-center text-sm text-gray-500">
                    <span>Created by</span>
                    <Link
                      to={`/profile/${community.creator.username}`}
                      className="ml-1 flex items-center"
                    >
                      <img
                        src={community.creator.profilePicture}
                        alt={community.creator.name}
                        className="w-5 h-5 rounded-full mr-1"
                      />
                      <span className="text-blue-600 hover:underline">
                        {community.creator.name}
                      </span>
                    </Link>
                  </div>
                )}
              </div>
            </div>
            
            {/* Community tabs */}
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="border-b border-gray-200">
                <nav className="flex">
                  <button
                    onClick={() => handleTabChange('posts')}
                    className={`px-6 py-4 text-center text-sm font-medium ${
                      activeTab === 'posts'
                        ? 'text-blue-600 border-b-2 border-blue-600'
                        : 'text-gray-500 hover:text-gray-700 hover:border-gray-300 border-b-2 border-transparent'
                    }`}
                  >
                    Posts
                  </button>
                  <button
                    onClick={() => handleTabChange('about')}
                    className={`px-6 py-4 text-center text-sm font-medium ${
                      activeTab === 'about'
                        ? 'text-blue-600 border-b-2 border-blue-600'
                        : 'text-gray-500 hover:text-gray-700 hover:border-gray-300 border-b-2 border-transparent'
                    }`}
                  >
                    About
                  </button>
                  <button
                    onClick={() => handleTabChange('members')}
                    className={`px-6 py-4 text-center text-sm font-medium ${
                      activeTab === 'members'
                        ? 'text-blue-600 border-b-2 border-blue-600'
                        : 'text-gray-500 hover:text-gray-700 hover:border-gray-300 border-b-2 border-transparent'
                    }`}
                  >
                    Members
                  </button>
                </nav>
              </div>
            </div>
            
            {/* Tab content */}
            {isPrivateAndNotMember ? (
              <div className="bg-white border border-gray-200 rounded-lg p-6 text-center">
                <svg
                  className="mx-auto h-12 w-12 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                </svg>
                <h3 className="mt-2 text-lg font-medium text-gray-900">Private Community</h3>
                <p className="mt-1 text-sm text-gray-500">
                  This is a private community. Join to see its content.
                </p>
                {isAuth ? (
                  <div className="mt-6">
                    <Button variant="primary" onClick={handleMembershipToggle}>
                      Join Community
                    </Button>
                  </div>
                ) : (
                  <div className="mt-6">
                    <Link to="/login">
                      <Button variant="primary">Log in to Join</Button>
                    </Link>
                  </div>
                )}
              </div>
            ) : (
              <>
                {/* Posts tab */}
                {activeTab === 'posts' && (
                  <div className="space-y-6">
                    {/* Post form for members */}
                    {isAuth && isMember && (
                      <PostForm
                        communityId={community._id}
                        onSuccess={handlePostCreated}
                      />
                    )}
                    
                    {/* Posts list */}
                    {error.posts && (
                      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                        {error.posts}
                      </div>
                    )}
                    
                    {loading.posts && posts.length === 0 ? (
                      <div className="bg-white border border-gray-200 rounded-lg p-6">
                        <div className="flex justify-center py-8">
                          <Loader size="lg" />
                        </div>
                      </div>
                    ) : posts.length === 0 ? (
                      <div className="bg-white border border-gray-200 rounded-lg p-6 text-center">
                        <svg
                          className="mx-auto h-12 w-12 text-gray-400"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                          />
                        </svg>
                        <h3 className="mt-2 text-lg font-medium text-gray-900">No posts yet</h3>
                        <p className="mt-1 text-sm text-gray-500">
                          Be the first to post in this community!
                        </p>
                        {isAuth && isMember && (
                          <div className="mt-6">
                            <Button
                              variant="primary"
                              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                            >
                              Create a Post
                            </Button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <>
                        <div className="space-y-4">
                          {posts.map((post) => (
                            <PostCard key={post._id} post={post} />
                          ))}
                        </div>
                        
                        {hasMore && (
                          <div className="flex justify-center mt-6">
                            <Button
                              onClick={handleLoadMore}
                              variant="light"
                              isLoading={loading.posts}
                            >
                              Load More
                            </Button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
                
                {/* About tab */}
                {activeTab === 'about' && (
                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <div className="space-y-6">
                      {/* Description */}
                      <div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">About</h3>
                        <p className="text-gray-700 whitespace-pre-wrap">
                          {community.description}
                        </p>
                      </div>
                      
                      {/* Rules */}
                      {community.rules && community.rules.length > 0 && (
                        <div>
                          <h3 className="text-lg font-medium text-gray-900 mb-2">Community Rules</h3>
                          <div className="space-y-4">
                            {community.rules.map((rule, index) => (
                              <div key={index} className="bg-gray-50 p-4 rounded-lg">
                                <h4 className="font-medium text-gray-900">
                                  {index + 1}. {rule.title}
                                </h4>
                                <p className="mt-1 text-gray-700">{rule.description}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Creation date */}
                      <div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">Created</h3>
                        <p className="text-gray-700">
                          {new Date(community.createdAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          })}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Members tab */}
                {activeTab === 'members' && (
                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Members</h3>
                    <p className="text-gray-700">
                      This community has {community.memberCount || 0} members.
                    </p>
                    
                    {/* Members list would go here */}
                    <div className="mt-4 text-center text-gray-500">
                      <p>Member list is coming soon!</p>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
          
          {/* Right sidebar */}
          <div className="hidden lg:block lg:w-80">
            <RightBar />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommunityDetail;